import { useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import {
  InstallmentStatusEnum,
  type Installment,
} from "@/generated/types";
import { PROJECT_CONTRACTS_QUERY } from "@/hooks/useProjectContracts";
import { PROJECT_FINANCIAL_SUMMARY_QUERY } from "@/hooks/useProjectFinancialSummary";
import { dateOnlyToUtcIsoDateTime } from "@/lib/dateInput";

export const MARK_INSTALLMENT_AS_PAID_MUTATION = gql`
  mutation MarkInstallmentAsPaid($input: MarkInstallmentAsPaidInput!) {
    markInstallmentAsPaid(input: $input) {
      installment {
        id
        number
        amount
        dueDate
        status
        paidAt
      }
    }
  }
`;

export type MutationCallbacks = {
  onCompleted?: () => void;
  onError?: (error: Error) => void;
};

type UseMarkInstallmentAsPaidOptions = MutationCallbacks;

export function useMarkInstallmentAsPaid(
  projectId: string,
  options?: UseMarkInstallmentAsPaidOptions
) {
  const [mutate, { loading, error }] = useMutation(MARK_INSTALLMENT_AS_PAID_MUTATION, {
    refetchQueries: [
      { query: PROJECT_CONTRACTS_QUERY, variables: { projectId } },
      { query: PROJECT_FINANCIAL_SUMMARY_QUERY, variables: { projectId } },
    ],
  });

  const markAsPaid = (
    installment: Pick<Installment, "id" | "number" | "amount" | "dueDate">,
    paidAtDate?: string,
    callbacks?: MutationCallbacks
  ) => {
    const paidAt = paidAtDate ? dateOnlyToUtcIsoDateTime(paidAtDate) : new Date().toISOString();

    mutate({
      variables: { input: { id: installment.id, paidAt } },
      optimisticResponse: {
        markInstallmentAsPaid: {
          __typename: "MarkInstallmentAsPaidPayload",
          installment: {
            __typename: "Installment",
            id: installment.id,
            number: installment.number,
            amount: installment.amount,
            dueDate: installment.dueDate,
            status: InstallmentStatusEnum.Paid,
            paidAt,
          },
        },
      },
      onCompleted: () => {
        options?.onCompleted?.();
        callbacks?.onCompleted?.();
      },
      onError: (mutationError) => {
        options?.onError?.(mutationError);
        callbacks?.onError?.(mutationError);
      },
    });
  };

  return { markAsPaid, loading, error };
}
